import React, { useState, useEffect, useMemo } from 'react'
import tw, { styled } from 'twin.macro'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import {Avatar} from 'baseui/avatar'
import {toaster} from 'baseui/toast'
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Progress,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'

import { ModalWrapper, ModalButtonWrapper } from '@/components/Modal'
import { Spinner } from '@/components/Spinner'
import {useNamedBoundingRectRef, useProgressControl} from '@/components/Tour'
import { PlatformEnum, ATTRIBUTE_LIST } from '@/types/nftAttribute'
import { useAttrEstimatedValue } from '@/hooks/useAttrEstimatedValue'
import { currentAccountAtom } from '@/features/identity/atoms'
import { mcpEndpointPrefixAtom, pwapiEndpointPrefixAtom } from '@/features/spirit/atoms/hosts'
import { queryMcpLinkedData, queryMcpSignConfigData, querySpiritAttributes } from '@/features/spirit/queries'

import { injectModalVisibleAtom } from '../atoms/injectModalVisibleAtom'
import { claimSpiritStageAtom, ClaimSpiritStageEnum } from '../atoms/claimSpiritStageAtom'
import { queryClientAtom } from 'jotai/query'

const PlatformData = [
  {
    icon: '/static/images/indexPage/01_step01/platform/khala.png',
    key: PlatformEnum.Khala,
    title: 'Khala Account',
    desc: [
      `Contributions to Phala's Kusama and Polkadot slot auctions.`,
      'Participation on Khala governance.',
      '$K-PHA holding balance, days and transfer times on Khala chain.',
      'Delegation amount in Phala stakepool.',
      'Running worker amount and mining rewards in stakepool.',
    ],
  },
  {
    icon: '/static/images/indexPage/01_step01/platform/discord.png',
    key: PlatformEnum.Discord,
    title: 'Discord',
    desc: [
      'Your level in PhalaNetwork and PhalaWorld.',
      `Team's, Ambassador's and Mod's contribution.`,
    ],
  },
  {
    icon: '/static/images/indexPage/01_step01/platform/twitter.png',
    key: PlatformEnum.Twitter,
    title: 'Twitter',
    desc: ['Your followers.'],
  },
  {
    icon: '/static/images/indexPage/01_step01/platform/github.png',
    key: PlatformEnum['Github'],
    title: 'Github',
    desc: ['Contributions to PhalaNetwork Github.'],
  },
  {
    icon: '/static/images/indexPage/01_step01/platform/phala.png',
    key: PlatformEnum['Forum'],
    title: 'Forum',
    desc: ['Badges amount.'],
  },
  {
    icon: '/static/images/indexPage/01_step01/platform/eth.png',
    key: PlatformEnum.ETH,
    title: 'ETH Account',
    desc: [
      '$PHA holding balance, days and transfer times on ETH chain.',
      'Phala Polkapet and Pill holders.',
    ],
  },
  {
    icon: '/static/images/indexPage/01_step01/platform/bsc.png',
    key: PlatformEnum.BSC,
    title: 'BSC Account',
    desc: ['$PHA holding balance, days and transfer times on BSC chain.'],
  },
]


const PlatformListWrapper = styled.div`
  ::-webkit-scrollbar {
    width: 44px;
  }

  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 4px 4px #363636;
    border-left: solid 40px transparent;
  }

  ::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 4px 4px #a8a8a8;
    border-left: solid 40px transparent;
  }
`

const PlatfromWrapper = styled.div`
  display: flex;
  gap: 23px;
  padding-bottom: 30px;
`

const PlatfromIcon = styled.div``

const PlatfromInfo = styled.div`
  flex: 1;
`
const InfoHead = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 12px;
`
const Title2 = styled.div`
  ${tw`font-secondary font-medium`}
  font-size: 20px;
  color: #ffffff;
  letter-spacing: 0.4px;
`
const Status = styled.div`
  ${tw`font-primary`}
  font-size: 14px;
  color: #575757;
`

const Desc = styled.div`
  ${tw`font-primary`}
  font-size: 14px;
  color: #b5b5b5;
  line-height: 21px;
`

const PlatformList: React.FC = () => {
  const mcpPrefix = useAtomValue(mcpEndpointPrefixAtom)
  const account = useAtomValue(currentAccountAtom)
  const {data: platformData} = useQuery(queryMcpLinkedData(mcpPrefix, account?.address || ''))

  return (
    <PlatformListWrapper tw="max-h-[50vh] flex-1 overflow-y-scroll">
      {PlatformData.map((item) => (
        <PlatfromWrapper key={item.title}>
          <PlatfromIcon>
            <Avatar name={item.icon} size="45px" src={item.icon} />
          </PlatfromIcon>
          <PlatfromInfo>
            <InfoHead>
              <Title2>{item.title}</Title2>
              {platformData && item.key in platformData ? (
                <Status style={{color: '#36fad7'}}>CONNECTED</Status>
              ) : (
                <Status>NOT CONNECTED</Status>
              )}
            </InfoHead>
            {item.desc.map((desc, index) => (
              <Desc key={index}>{desc}</Desc>
            ))}
          </PlatfromInfo>
        </PlatfromWrapper>
      ))}
    </PlatformListWrapper>
  )
}

const Title = styled.div`
  ${tw`font-secondary font-bold`}
  font-size: 20px;
  color: #ffffff;
  padding-bottom: 30px;

  small {
    ${tw`block font-primary text-xs font-light mt-2`}
  }
`

const DataBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const DataName = styled.span`
  ${tw`font-secondary`}
  font-size: 14px;
  color: #ffffff;
`

const DescInfo = styled.div`
  ${tw`font-primary`}
  font-size: 14px;
  color: #b5b5b5;
  line-height: 20px;
`

const Content = styled.div`
  ${tw`font-primary`}
  font-size: 16px;
  line-height: 150%;
  color: #fff;
`

const _ModalFooter = styled.div`
  ${tw`flex flex-row gap-1`}
  padding: 30px 0 0;
`

const isUpdatingSpiritAtom = atom(async (get) => {
  const queryClient = get(queryClientAtom)
  const pwApiPrefix = get(pwapiEndpointPrefixAtom)
  const currentAccount = get(currentAccountAtom)
  const data = await queryClient.fetchQuery(querySpiritAttributes(pwApiPrefix, currentAccount?.address || ''))
  if (data && data.extra && data.extra.stats_refreshing_at) {
    const time1 = data.stats_refreshed_at
      ? new Date(data.stats_refreshed_at).getTime()
      : 0
    const time2 = new Date(data.extra.stats_refreshing_at).getTime()
    if (time1 < time2) {
      return true
    }
  }
  return false
})

const useInjectUrl = () => {
  const currentAccount = useAtomValue(currentAccountAtom)
  const pwPrefix = useAtomValue(pwapiEndpointPrefixAtom)
  const {data: configData} = useQuery(queryMcpSignConfigData(pwPrefix))
  /**
   * MCP Authorization wiki:
   * https://litentry.notion.site/How-to-integrate-with-MCP-e522db9747b54a0a9e6bf2b0fe4b1fe6
   */
  return useMemo(() => {
    // dispatch('cancel')
    // setContinueModalVisible(true)
    // mcp authorize
    if (!currentAccount?.address) {
      toaster.info('Please select an polkadot account first', {})
      return
    }
    const redirectUrl = `${window.location.origin}/claim/`
    const authId = configData?.mcp_auth_id
    if (!authId) {
      console.error('AuthId missed.')
      return ''
    }
    return `https://mycryptoprofile.io/register?redirect=${redirectUrl}&authId=${authId}`
    // gtag('event', 'claim/identity/mcp_auth')
    // window.open(
    //   `https://mycryptoprofile.io/register?redirect=${redirectUrl}&authId=${authId}`,
    //   // `https://staging.mycryptoprofile.io/register?redirect=${redirectUrl}&authId=${authId}`,
    //   '_blank'
    // )
  }, [configData, currentAccount])
}

const EstimatedValue: React.FC = () => {
  const stage = useAtomValue(claimSpiritStageAtom)

  const attrValue = useAttrEstimatedValue()

  const [continueModalVisible, setContinueModalVisible] = useState(false)
  const isUpdatingSpirit = useAtomValue(isUpdatingSpiritAtom)
  const setInjectionModalVisible = useSetAtom(injectModalVisibleAtom)

  const ref = useNamedBoundingRectRef<HTMLButtonElement>('registerSpiritHint')
  const dispatch = useProgressControl('registerSpiritHint')
  useEffect(() => {
    const seed = setTimeout(() => {
      if (stage === ClaimSpiritStageEnum.original) {
        dispatch('next')
      }
    }, 250)
    return () => {
      clearTimeout(seed)
      dispatch('cancel')
    }
  }, [dispatch, stage])

  const authURL = useInjectUrl()

  return (
    <>
      <div tw="flex-1 pb-8">
        <Title>
          Data completion progress
          <small>
            Shows the account linking progress that affects this attribute.
          </small>
        </Title>
        {attrValue &&
          ATTRIBUTE_LIST.map((data) => (
            <div key={data} tw="pb-1.5 flex flex-col gap-2">
              <DataBar>
                <DataName>{data}</DataName>
              </DataBar>
              <Progress
                value={attrValue[data].currentValue / attrValue[data].successValue * 100}
                size="xs"
              />
            </div>
          ))}
        <Button as="a" w="100%" mt="4" mb="8" href={authURL} target="_blank">
          {stage === ClaimSpiritStageEnum.original
            ? 'GENERATE IDENTITY'
            : 'INJECT MORE DATA'}
        </Button>
        <DescInfo>
          Spirit data is the maximum value you link in My Crypto Profile.
        </DescInfo>
        <DescInfo>
          Please ensure PhalaWorld and My Crypto Profile are logged in to the
          same account. Otherwise claim will fail.
        </DescInfo>
        <DescInfo style={{marginTop: '8px'}}>We support :</DescInfo>
        <DescInfo style={{color: '#36fad7'}}>
          Discord, Forum, Twitter, Github, Khala chain, ETH chain, BSC chain.
        </DescInfo>
        <DescInfo style={{color: '#575757', marginTop: '8px'}}>
          Pubilc information only
        </DescInfo>
      </div>
      <ModalWrapper visible={continueModalVisible} title="Confirm">
        <Content>Please generate identity first.</Content>
        <ModalFooter>
          <ModalButtonWrapper onClick={() => setContinueModalVisible(false)}>
            Cancel
          </ModalButtonWrapper>
          <ModalButtonWrapper
            disabled={isUpdatingSpirit}
            onClick={() => {
              setContinueModalVisible(false)
              setInjectionModalVisible(false)
            }}
          >
            {isUpdatingSpirit ? (
              <Spinner />
            ) : (
              'Continue'
            )}
          </ModalButtonWrapper>
        </ModalFooter>
      </ModalWrapper>
    </>
  )
}

const ModalTitle = styled.div`
  ${tw`font-secondary font-bold`}
  font-size: 24px;
  color: #ffffff;
  line-height: 14px;
  letter-spacing: 0.48px;
`

const LearnMore = styled.div`
  a {
    ${tw`font-primary`}
    font-size: 16px;
    color: #36fad7;
    line-height: 14px;
    letter-spacing: 0.32px;
    cursor: pointer;
    outline: none;
    padding-bottom: 2px;
    border-bottom: 1px solid transparent;
    transition: 0.5s ease;

    &:hover {
      border-bottom: 1px solid #36fad7;
    }
  }
`

export default function InjectModal() {
  const [isOpen, setIsOpen] = useAtom(injectModalVisibleAtom)
  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <div tw="flex flex-row gap-2 items-center">
            <ModalTitle>DATA INJECTION</ModalTitle>
            <LearnMore>
              <a
                href="https://docs.phala.world/introduction/the-spirit-nft/attribute-logicenen"
                rel="noreferrer"
                target="_blank"
              >
                LEARN MORE
              </a>
            </LearnMore>
          </div>
          <div tw="text-sm text-gray-400 my-2 text-left">
            Just connect to the account and we will find your corresponding
            identity data.
          </div>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <div tw="flex flex-row gap-10">
            <PlatformList />
            <EstimatedValue />
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
